import React, { useCallback, useMemo, memo, useState, useEffect } from "react";
import { PanelLeftClose, GripVertical, Settings2 } from "lucide-react";
import { PresentationSettings } from "@/components/slides/presentation-settings";
import { SlideSettings } from "@/components/slides/slide-settings";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useSlideStore } from "@/stores/slide-store";
import type { SlideInfo } from "@/stores/slide-store";
import { previewMessage } from "@/lib/visual-editor/post-message";
import { Hint } from "@/components/ui/hint";

import { claudeAgentApi } from "@/lib/api/claude-agent";

interface SlidesPanelProps {
  previewIframeRef: React.RefObject<HTMLIFrameElement | null>;
  previewUrl?: string;
  projectId?: string;
  devContainerId?: string;
  getIdToken: () => Promise<string | null>;
}

export function SlidesPanel({
  previewIframeRef,
  previewUrl,
  projectId,
  devContainerId,
  getIdToken,
}: SlidesPanelProps) {
  const {
    slides,
    currentSlideIndex,
    isSidebarCollapsed,
    setSidebarCollapsed,
    thumbnailVersion,
    bumpThumbnailVersion,
    setSlides,
  } = useSlideStore();

  // Build thumbnail base URL from previewUrl — used as origin for serving .slide-thumbnails/
  const previewOrigin = useMemo(() => {
    if (!previewUrl) return null;
    try {
      const url = new URL(previewUrl);
      return url.origin;
    } catch {
      return null;
    }
  }, [previewUrl]);

  // Thumbnails are displayed from slide.thumbnail field in the manifest.
  // Capture is handled server-side by auto-commit subscriber → sidecar Puppeteer.
  // Frontend just bumps version when slides load to trigger img refresh.
  const slidesFingerprint = [...slides.map((s) => s.id)].sort().join(",");
  useEffect(() => {
    if (slides.length > 0) bumpThumbnailVersion();
  }, [slidesFingerprint]);

  const handleSlideClick = useCallback(
    (index: number) => {
      previewMessage.navigateToSlide(previewIframeRef.current, index);
      useSlideStore.getState().setCurrentSlide(index);
    },
    [previewIframeRef],
  );

  // DnD sensors with 5px activation distance to prevent accidental drags
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = slides.findIndex((s) => s.id === active.id);
      const newIndex = slides.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      // Optimistic update
      const newSlides = arrayMove(slides, oldIndex, newIndex);
      setSlides(newSlides);

      // Persist to sidecar
      if (!devContainerId) return;
      try {
        const token = await getIdToken();
        if (!token) return;
        await claudeAgentApi.reorderSlides(
          token,
          devContainerId,
          newSlides.map((s) => s.id),
        );
      } catch (err) {
        // Revert on failure
        console.error("Failed to reorder slides:", err);
        setSlides(slides);
      }
    },
    [slides, devContainerId, getIdToken, setSlides],
  );

  const slideIds = useMemo(() => slides.map((s) => s.id), [slides]);

  return (
    <div
      className={cn(
        "border-r border-border bg-card flex flex-col transition-all duration-200",
        isSidebarCollapsed
          ? "w-0 min-w-0 overflow-hidden border-r-0"
          : "w-[160px] min-w-[160px]",
      )}
    >
      {/* Header — matches top bar height */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <span className="text-xs font-medium text-foreground">Slides</span>
        <div className="flex items-center gap-0.5">
          <PresentationSettings
            previewIframeRef={previewIframeRef}
            devContainerId={devContainerId}
            getIdToken={getIdToken}
          />
          <Hint
            delayDuration={0}
            text="Hide slides"
            side="right"
            align="center"
          >
            <button
              onClick={() => setSidebarCollapsed(true)}
              className="p-1 hover:bg-accent rounded transition-colors"
            >
              <PanelLeftClose className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          </Hint>
        </div>
      </div>

      {/* Slide List */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1.5">
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <SortableContext
              items={slideIds}
              strategy={verticalListSortingStrategy}
            >
              {slides.map((slide: SlideInfo, index: number) => (
                <SortableSlideThumbnail
                  key={slide.id}
                  slide={slide}
                  index={index}
                  isCurrent={index === currentSlideIndex}
                  onClick={() => handleSlideClick(index)}
                  previewOrigin={previewOrigin}
                  thumbnailVersion={thumbnailVersion}
                  previewIframeRef={previewIframeRef}
                  devContainerId={devContainerId}
                  getIdToken={getIdToken}
                />
              ))}
            </SortableContext>
          </DndContext>
          {slides.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-4">
              No slides yet
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

interface SlideThumbnailProps {
  slide: SlideInfo;
  index: number;
  isCurrent: boolean;
  onClick: () => void;
  previewOrigin: string | null;
  thumbnailVersion: number;
  previewIframeRef: React.RefObject<HTMLIFrameElement | null>;
  devContainerId?: string;
  getIdToken: () => Promise<string | null>;
}

const SortableSlideThumbnail = memo(function SortableSlideThumbnail({
  slide,
  index,
  isCurrent,
  onClick,
  previewOrigin,
  thumbnailVersion,
  previewIframeRef,
  devContainerId,
  getIdToken,
}: SlideThumbnailProps) {
  const [imgError, setImgError] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slide.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Reset error state when thumbnailVersion changes (new thumbnails generated)
  useEffect(() => {
    setImgError(false);
  }, [thumbnailVersion]);

  const thumbnailPath =
    slide.thumbnail || `.slide-thumbnails/t-${slide.id}.jpg`;
  const thumbnailSrc = previewOrigin
    ? `${previewOrigin}/${thumbnailPath}?v=${thumbnailVersion}`
    : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "w-full flex items-start gap-1 group rounded-md px-1 py-1 transition-colors",
        isCurrent ? "bg-accent/50" : "hover:bg-accent/30",
        isDragging && "shadow-lg ring-2 ring-primary z-10",
      )}
    >
      {/* Drag handle + slide number */}
      <div className="flex flex-col items-center mt-1.5 min-w-[14px]">
        <span
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-0.5 -mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <GripVertical className="w-3 h-3 text-muted-foreground" />
        </span>
        <span
          className={cn(
            "text-[10px] font-medium tabular-nums",
            isCurrent ? "text-primary" : "text-muted-foreground",
          )}
        >
          {index + 1}
        </span>
      </div>

      {/* Thumbnail */}
      <button
        onClick={onClick}
        className={cn(
          "flex-1 aspect-video rounded overflow-hidden transition-all relative cursor-pointer",
          isCurrent
            ? "ring-2 ring-primary shadow-sm"
            : "ring-1 ring-border group-hover:ring-muted-foreground/50",
        )}
      >
        {thumbnailSrc && !imgError ? (
          <img
            src={thumbnailSrc}
            alt={`Slide ${index + 1}: ${slide.title}`}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full bg-muted flex items-center justify-center">
            <span className="text-[8px] text-muted-foreground leading-tight text-center px-1 line-clamp-2">
              {slide.title}
            </span>
          </div>
        )}
        {/* Per-slide settings gear (hover) */}
        <SlideSettings
          slide={slide}
          previewIframeRef={previewIframeRef}
          devContainerId={devContainerId}
          getIdToken={getIdToken}
        >
          <button
            onClick={(e) => e.stopPropagation()}
            className="absolute top-0.5 right-0.5 p-0.5 rounded bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/60"
          >
            <Settings2 className="w-2.5 h-2.5 text-white" />
          </button>
        </SlideSettings>
      </button>
    </div>
  );
});
